export const template = (header, data) => {

	// Format numbers consistently with 2 decimals
	const format = (val) =>
		val !== null && val !== undefined
			? Number(val).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
			: "";

	// Generate row labels: A-Z, AA, AB...
	const getRowLabel = (i) => {
		let label = '';
		while (i >= 0) {
			label = String.fromCharCode(65 + (i % 26)) + label;
			i = Math.floor(i / 26) - 1;
		}
		return label;
	};

	// Sort deduction groups by position
	const sortedGroups = data?.deductionGroups
		? [...data.deductionGroups].sort((a, b) => a.position - b.position)
		: [];

	return `
<style>
/* ===== GENERAL STYLES ===== */
body {
  font-family: Arial, sans-serif;
  margin: 0;
  padding: 0;
  line-height: 1; /* compact spacing */
}

.customcontainer {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0; /* remove extra padding */
}

table {
  width: 100%;
  border-collapse: collapse; /* remove spacing between cells */
  margin: 0; /* remove default margins */
  padding: 0;
}

th, td {
  border: 1px solid black;
  font-size: 12px;
  padding: 2px 1px;
  text-align: left;
  line-height: 1;
}

.header-img {
  width: 100%;
  height: auto;
}

.borderless {
  border: none !important;
}

.br td, .br th {
  border-right: 1px solid black !important;
  border-left: 1px solid black !important;
}

.btless {
  border-top: none !important;
}

.signature {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
}

.signature div {
  width: 45%;
}

/* ===== REMOVE EXTRA SPACING BETWEEN TABLES ===== */
table + table {
  margin-top: 0; /* remove spacing between consecutive tables */
}

/* ===== PRINT STYLES ===== */
@media print {
  @page {
    size: A4;
    margin: 5mm;
  }

  body {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-size: 9px;
    line-height: 1;
  }

  .customcontainer {
    width: 210mm;
    height: 297mm;
    padding: 0; /* remove container padding for print */
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  table {
    font-size: 9px;
    margin: 0; /* remove table margin */
    border-collapse: collapse;
    page-break-inside: avoid; /* keep table rows together */
  }

  th, td {
    padding: 1mm 0.5mm; /* compact padding */
    margin: 0; /* remove extra spacing */
  }

  .header-img {
    max-width: 100%;
    height: auto;
  }

  .signature {
    margin-top: 5px; /* small margin only */
  }

  .no-page-break {
    page-break-inside: avoid; /* prevent breaking important blocks */
  }
}
</style>

<div class="customcontainer">

<!-- HEADER -->
<table class="custom-table m-0 p-0">
  <tr>
    <td><img src="${header}" class="header-img" /></td>
  </tr>
</table>

<!-- EMPLOYEE INFO -->
<table class="m-0 borderless">
<tr class="borderless br">
  <th style="width:19%">Name</th>
  <td>${data.employee?.fullName || ""}</td>
  <th>Employment Date</th>
  <td>${data.employee?.employmentDate || ""}</td>
  <th>Payment Mode</th>
  <td>Bank Transfer</td>
</tr>
<tr class="borderless br">
  <th>department</th>
  <td>${data.employee?.department || ""}</td>
  <th>gender</th>
  <td>${data.employee?.gender || ""}</td>
  <th>phone</th>
  <td>${data.employee?.phone || ""}</td>
</tr>
<tr class="borderless br">
  <th>Category</th>
  <td>${data.employee?.category || ""}</td>
  <th>Matrics</th>
  <td>${data.employee?.matricule || ""}</td>
  <th>CNPS No.</th>
  <td>${data.employee?.cnpsNumber || ""}</td>
</tr>
<tr class="borderless br">
  <th>Echelon</th>
  <td>${data.employee?.echelon || ""}</td>
  <th>Post</th>
  <td>${data.employee?.position || ""}</td>
  <th>Marital Status</th>
  <td>${data.employee?.maritalStatus || ""}</td>
</tr>
</table>

<!-- MAIN SALARY TABLE -->
<table class="btless blless">

<thead>
<tr>
  <th>S/N</th>
  <th>Description</th>
  <th>Base</th>
  <th>Rate%</th>
  <th>Benefits</th>
  <th>Taxable</th>
  <th>Non-Taxable</th>
  <th>Deduction</th>
  <th>Base</th>
  <th>Rate</th>
  <th>Amount</th>
</tr>
</thead>

<tbody>

<!-- BASIC SALARY -->
<tr class="br">
  <td>1</td>
  <td><strong>Basic Salary</strong></td>
  <td>${format(data.basicSalary)}</td>
  <td></td>
  <td>${format(data.basicSalary)}</td>
  <td>${data.taxable ? "Yes" : "No"}</td>
  <td></td>
  <td></td>
  <td>${format(data.basicSalary)}</td>
  <td></td>
  <td>${format(data.basicSalary)}</td>
</tr>

<!-- BONUSES -->
${data.bonuses?.map((bonus, i) => `
<tr class="br">
  <td>${i + 2}</td>
  <td>${bonus.name}</td>
  <td></td>
  <td>${bonus.percentage ? bonus.amount + "%" : ""}</td>
  <td>${format(bonus.amount)}</td>
  <td>${bonus.taxable ? "Yes" : "No"}</td>
  <td>${!bonus.taxable ? format(bonus.amount) : ""}</td>
  <td></td>
  <td></td>
  <td></td>
  <td>${format(bonus.amount)}</td>
</tr>
`).join("")}

<!-- DEDUCTION GROUPS -->
${sortedGroups.map(group => `
<tr class="br border">
  <th colspan="12">${group.name}</th>
</tr>
${group.deductions.map((deduction, i) => `
<tr class="br">
  <td>${getRowLabel(i)}</td>
  <td>${deduction.name}</td>
  <td>${format(data.grossSalary)}</td>
  <td>${deduction.percentage ? deduction.rateOrAmount + "%" : ""}</td>
  <td></td>
  <td></td>
  <td></td>
  <td>${format(deduction.rateOrAmount)}</td>
  <td>${format(data.grossSalary)}</td>
  <td>${deduction.percentage ? deduction.rateOrAmount + "%" : ""}</td>
  <td>${format(deduction.amountDeducted)}</td>
</tr>
`).join("")}
`).join("")}

<!-- OTHER DEDUCTIONS (REFUNDS) -->
${data.refunds?.length ? `
<tr class="br border">
  <th colspan="12">Other Deductions</th>
</tr>
${data.refunds.map((refund, i) => `
<tr class="br">
  <td>${getRowLabel(i)}</td>
  <td>${refund.name}</td>
  <td></td>
  <td></td>
  <td></td>
  <td></td>
  <td></td>
  <td>${format(refund.amount)}</td>
  <td></td>
  <td></td>
  <td>${format(refund.amount)}</td>
</tr>
`).join("")}
` : ''}

<!-- TOTAL ROW -->
<tr class="br">
  <th></th>
  <th>TOTAL</th>
  <th></th>
  <th></th>
  <th>${format(data.grossSalary)}</td>
  <th></th>
  <th></th>
  <th>${format(data.totalDeductions)}</td>
  <th></th>
  <th></th>
  <th>${format(data.netSalary)}</th>
</tr>

</tbody>

<tbody class="borderless">
<tr>
<td colspan="2">Gross Salary</td>
<td colspan="5">${format(data.grossSalary)} CFA</td>
<td colspan="4"></td>
</tr>
<tr>
<td colspan="2">Total Bonus</td>
<td colspan="5">${format(data.totalOfBonuses)} CFA</td>
<td colspan="4"></td>
</tr>
<tr>
<td colspan="2">Total Deduction</td>
<td colspan="5">${format(data.totalDeductions)} CFA</td>
<td colspan="4"></td>
</tr>
<tr>
<td colspan="2">Total Refunds</td>
<td colspan="5">${format(data.totalOfRefunds)} CFA</td>
<td colspan="4"></td>
</tr>
<tr>
<th colspan="2" style="font-size:18px;">Net Pay</th>
<th colspan="5" style="font-size:18px;">
${format(data.netSalary)} CFA
</th>
<td colspan="4"></td>
</tr>
</tbody>

</table>

<div class="signature">
<div>
<p>Sign Employer & Date</p>
<hr>
</div>
<div>
<p>Sign Employee & Date</p>
<hr>
</div>
</div>

</div>
`;
};